import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { translate } from './translate';
import {
  LOCALES, LOCALE_KEY, DEFAULT_LOCALE, catalogFor, resolveInitialLocale,
} from './locales';

/**
 * English, for a component rendered with no provider above it.
 *
 * Throwing here would be the stricter choice and it is the wrong one: this
 * codebase renders components standalone in dozens of test files, and a
 * provider in each is a tax paid forever for a mistake that shows up the first
 * time anybody opens the app. English is the fallback catalog in any case, so
 * the degradation is to exactly the language the strings are written in —
 * everything renders, only the switcher would be inert.
 */
const DEFAULT_CONTEXT = {
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  locales: LOCALES,
  t: (key, params) => translate(catalogFor(DEFAULT_LOCALE), null, key, params),
};

const LocaleContext = createContext(DEFAULT_CONTEXT);

function readStored() {
  try {
    return localStorage.getItem(LOCALE_KEY);
  } catch {
    // Blocked site data. English is a fine answer.
    return null;
  }
}

/**
 * The active language, and the `t` every component draws its text with.
 *
 * Locale is a display preference, exactly like layout: per device, stored in
 * localStorage, and never on the wire. Two players in one room can read the
 * same game in different languages, because nothing about it is shared state.
 */
export function LocaleProvider({ children, initial }) {
  const [locale, setLocaleState] = useState(() =>
    initial ?? resolveInitialLocale(readStored(), navigator.languages ?? [navigator.language]));

  const setLocale = useCallback(code => {
    setLocaleState(code);
    try {
      localStorage.setItem(LOCALE_KEY, code);
    } catch {
      // The choice still applies to this session; it just won't outlive it.
    }
  }, []);

  // Screen readers and hyphenation both read this, and it is the one piece of
  // the page that is not ours to render.
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);

  const value = useMemo(() => {
    const catalog = catalogFor(locale);
    const fallback = catalogFor(DEFAULT_LOCALE);
    return {
      locale,
      setLocale,
      locales: LOCALES,
      t: (key, params) => translate(catalog, fallback, key, params),
    };
  }, [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

function useLocaleContext() {
  return useContext(LocaleContext);
}

/** The translate function for the active locale. */
export function useT() {
  return useLocaleContext().t;
}

/** `{ locale, setLocale, locales }` — for the switcher, and nothing else. */
export function useLocale() {
  const { locale, setLocale, locales } = useLocaleContext();
  return { locale, setLocale, locales };
}
